import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, AlertTriangle } from "lucide-react";
import api, { apiErr, money } from "@/lib/api";
import { adminInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function EmailLinkDialog({ requestId, open, onOpenChange, onSent }) {
  const [prev, setPrev] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && requestId) {
      setPrev(null);
      api.get(`/admin/payments/requests/${requestId}/email-preview`)
        .then((r) => { setPrev(r.data); setMsg(r.data.guest_message || ""); })
        .catch((e) => toast.error(apiErr(e)));
    }
  }, [open, requestId]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/payments/requests/${requestId}/email`, { guest_message: msg });
      toast.success("Secure payment email Draft created — review & release to send.");
      onOpenChange(false);
      onSent && onSent(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const Row = ({ k, v }) => (
    <div className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10" data-testid="email-link-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Email Secure Payment Link</DialogTitle></DialogHeader>
        {!prev ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 className="animate-spin mx-auto" /></div>
        ) : prev.blocked_reason ? (
          <div className="rounded-md border border-red-400/40 bg-red-400/10 p-4 flex items-start gap-2" data-testid="email-blocked">
            <AlertTriangle size={16} className="text-red-300 mt-0.5" />
            <p className="text-sm text-red-200">{prev.blocked_reason}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            <Row k="Recipient" v={`${prev.recipient_name || ""} · ${prev.recipient_email}`} />
            <Row k="Purpose" v={prev.purpose_label} />
            <Row k="Amount" v={money(prev.amount)} />
            <Row k="Due date" v={prev.due_date || "—"} />
            <Row k="Reference" v={prev.ref_number || "—"} />
            <Row k="Secure link expires" v={(prev.expires_at || "").slice(0, 10) || "—"} />
            {prev.balance !== undefined && <>
              <div className="gold-line my-1" />
              <Row k="Current folio balance" v={money(prev.balance)} />
              <Row k="Amount already paid" v={money(prev.paid)} />
              <Row k="Remaining after payment" v={money(prev.remaining_after)} />
            </>}
            <div className="pt-1">
              <label className="text-xs text-muted-foreground">Guest-facing message (editable)</label>
              <textarea className={adminInput} rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="email-guest-message" />
            </div>
            <p className="text-[11px] text-muted-foreground">The authoritative amount cannot be changed here. This creates a branded Draft in Communications → Review &amp; Release; a human must approve it before it sends.</p>
            <button onClick={send} disabled={sending} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="email-link-create">
              {sending ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />} Create Email Draft
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
